/**
 * `/materials` — the learner's course materials (P-SCH-F).
 *
 * Reads `learning.learner.materials.list` (every material item across the
 * caller's enrolled, published courses, returned all at once) and presents
 * them grouped by course. Client-side filters narrow the list without a
 * refetch: a free-text search, a kind filter (All / Pages / Files / Videos /
 * Links), and a native `<select>` of the DISTINCT courses present in the result.
 *
 * Behaviour by kind:
 *   - link  → an `<a target="_blank">` to `externalUrl` (new tab).
 *   - video → same as link when `externalUrl` is set (label "Watch").
 *   - page  → inline-expands the markdown body under the row via `<RichText>`,
 *             fetched when opened (`<PageBody>`, #952).
 *   - file  → inline-expands a `<MaterialFilePreview>` (image / PDF / download).
 *
 * Inline expansion (not a modal) keeps it reliable + keyboard-trivial: one
 * `openItemId` in state, a real `<button>` toggle, Escape collapses it. The
 * list proc is NOT gated by `learnerAnalyticsEnabled`, so there's no
 * FORBIDDEN → /catalog fallback here (unlike `/home`).
 *
 * Kind vocabulary is inlined (string checks) — this SPA must never import
 * `@mangrove/core`.
 */
import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import type { inferRouterOutputs } from '@trpc/server';
import {
  ChevronDown,
  ExternalLink,
  File as FileIcon,
  FileText,
  FolderOpen,
  Link as LinkIcon,
  Paperclip,
  Search,
  Video,
  XCircle,
} from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  RichText,
  Skeleton,
  buttonVariants,
  cn,
} from '@mangrove/ui';
import { trpc, type AppRouter } from '../lib/trpc.js';
import { RequireAuth } from '../lib/auth.js';
import { LearnShell } from '../components/shell.js';
import { MaterialFilePreview } from '../components/materials/material-file-preview.js';
import { PageBody } from '../components/materials/page-body.js';
import { useMarkSeen } from '../components/materials/use-mark-seen.js';

export const Route = createFileRoute('/materials/')({
  component: MaterialsPage,
});

type Material =
  inferRouterOutputs<AppRouter>['learning']['learner']['materials']['list']['items'][number];

type KindFilter = 'all' | 'page' | 'file' | 'video' | 'link';

const KIND_TABS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'page', label: 'Pages' },
  { value: 'file', label: 'Files' },
  { value: 'video', label: 'Videos' },
  { value: 'link', label: 'Links' },
];

function MaterialsPage(): React.JSX.Element {
  return (
    <RequireAuth>
      <LearnShell title="Materials" wide>
        <MaterialsContent />
      </LearnShell>
    </RequireAuth>
  );
}

function MaterialsContent(): React.JSX.Element {
  const listQ = trpc.learning.learner.materials.list.useQuery();
  const [kind, setKind] = React.useState<KindFilter>('all');
  const [courseId, setCourseId] = React.useState<string>('');
  const [search, setSearch] = React.useState<string>('');
  const [openItemId, setOpenItemId] = React.useState<string | null>(null);

  const items = listQ.data?.items ?? [];

  // DISTINCT courses present in the result, title-sorted, for the <select>.
  const courses = React.useMemo(() => {
    const seen = new Map<string, string>();
    for (const m of items) {
      if (!seen.has(m.courseId)) seen.set(m.courseId, m.courseTitle);
    }
    return [...seen.entries()]
      .map(([id, title]) => ({ id, title }))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [items]);

  // Per-kind counts for the segmented filter chips (over the unfiltered list).
  const kindCounts = React.useMemo(() => {
    const counts: Record<KindFilter, number> = {
      all: items.length,
      page: 0,
      file: 0,
      video: 0,
      link: 0,
    };
    for (const m of items) {
      if (m.kind === 'page' || m.kind === 'file' || m.kind === 'video' || m.kind === 'link') {
        counts[m.kind] += 1;
      }
    }
    return counts;
  }, [items]);

  // Client-side filter: by kind, then by course, then by free-text search.
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter(
      (m) =>
        (kind === 'all' || m.kind === kind) &&
        (courseId === '' || m.courseId === courseId) &&
        (q === '' || m.title.toLowerCase().includes(q) || m.courseTitle.toLowerCase().includes(q)),
    );
  }, [items, kind, courseId, search]);

  // Group the filtered items by course, preserving courses' title order.
  // Keyed by courseId (not title) so two courses sharing a title never collide.
  const groups = React.useMemo(() => {
    const byCourse = new Map<string, { id: string; title: string; items: Material[] }>();
    for (const m of filtered) {
      const group = byCourse.get(m.courseId);
      if (group) group.items.push(m);
      else byCourse.set(m.courseId, { id: m.courseId, title: m.courseTitle, items: [m] });
    }
    return [...byCourse.values()].sort((a, b) => a.title.localeCompare(b.title));
  }, [filtered]);

  function toggleOpen(id: string): void {
    setOpenItemId((cur) => (cur === id ? null : id));
  }

  return (
    <div className="space-y-6">
      <div className="animate-fade-in space-y-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-primary">Library</p>
        <h2 className="text-xl font-semibold tracking-tight">Materials</h2>
        <p className="text-sm text-muted-foreground">
          Readings, files, slides, and recordings shared by your instructors.
        </p>
      </div>

      {listQ.isPending ? (
        <MaterialsSkeleton />
      ) : listQ.isError ? (
        <ErrorCard
          message="We couldn’t load your materials. Please check your connection and try again."
          onRetry={() => void listQ.refetch()}
        />
      ) : items.length === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title="No materials yet"
          description="Readings, files, and links your instructors add to your courses will appear here."
        />
      ) : (
        <div className="space-y-6">
          {/* Toolbar: search + kind segmented filter + course select */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
            <div className="relative min-w-0 flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search materials…"
                aria-label="Search materials"
                className="min-h-touch pl-9"
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div
                role="group"
                aria-label="Filter materials by kind"
                className="inline-flex flex-wrap items-center gap-1 rounded-lg border bg-card p-1"
              >
                {KIND_TABS.map((tab) => {
                  const active = kind === tab.value;
                  return (
                    <button
                      key={tab.value}
                      type="button"
                      onClick={() => setKind(tab.value)}
                      aria-pressed={active}
                      className={cn(
                        'press inline-flex min-h-touch items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        active
                          ? 'bg-primary/10 text-primary'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {tab.label}
                      <span
                        className={cn(
                          'tabular-nums text-xs',
                          active ? 'text-primary' : 'text-muted-foreground',
                        )}
                      >
                        {kindCounts[tab.value]}
                      </span>
                    </button>
                  );
                })}
              </div>

              {courses.length > 1 ? (
                <select
                  value={courseId}
                  onChange={(e) => setCourseId(e.target.value)}
                  aria-label="Filter materials by course"
                  className="press min-h-touch shrink-0 rounded-lg border border-input bg-card px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">All courses</option>
                  {courses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          </div>

          {groups.length === 0 ? (
            <EmptyState
              icon={FolderOpen}
              title="Nothing matches that filter"
              description="Try a different search, kind, or course."
            />
          ) : (
            <div className="ls-stagger space-y-8">
              {groups.map((group) => (
                <section key={group.id} className="space-y-3" aria-labelledby={`grp-${group.id}`}>
                  <div className="flex items-center gap-3 px-1">
                    <span
                      className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-brand-blue text-sm font-bold text-primary-foreground shadow-sm"
                      aria-hidden="true"
                    >
                      {courseInitials(group.title)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <h3
                        id={`grp-${group.id}`}
                        className="truncate text-base font-semibold tracking-tight"
                      >
                        {group.title}
                      </h3>
                      <p className="text-xs text-muted-foreground">
                        <span className="tabular-nums">{group.items.length}</span>{' '}
                        {group.items.length === 1 ? 'material' : 'materials'}
                      </p>
                    </div>
                  </div>

                  <Card className="card-soft overflow-hidden p-0">
                    <ul className="divide-y divide-border">
                      {group.items.map((m) => (
                        <MaterialRow
                          key={m.itemId}
                          material={m}
                          open={openItemId === m.itemId}
                          onToggle={() => toggleOpen(m.itemId)}
                        />
                      ))}
                    </ul>
                  </Card>
                </section>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Two-letter crest initials from a course title (e.g. "SAT Math" → "SA"). */
function courseInitials(title: string): string {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[1]![0]!).toUpperCase();
}

/** Per-kind icon + the tone tokens used for its icon chip + badge. */
function kindMeta(kind: string): {
  Icon: typeof FileText;
  label: string;
  chip: string;
  badge: 'default' | 'secondary' | 'outline';
} {
  switch (kind) {
    case 'page':
      return {
        Icon: FileText,
        label: 'Page',
        chip: 'bg-primary/10 text-primary',
        badge: 'secondary',
      };
    case 'file':
      return {
        Icon: Paperclip,
        label: 'File',
        chip: 'bg-brand-blue/10 text-brand-blue',
        badge: 'outline',
      };
    case 'video':
      return {
        Icon: Video,
        label: 'Video',
        chip: 'bg-brand-blue/10 text-brand-blue',
        badge: 'outline',
      };
    case 'link':
      return {
        Icon: LinkIcon,
        label: 'Link',
        chip: 'bg-warning/10 text-warning',
        badge: 'outline',
      };
    default:
      return {
        Icon: FileIcon,
        label: 'Item',
        chip: 'bg-muted text-muted-foreground',
        badge: 'outline',
      };
  }
}

function MaterialRow({
  material,
  open,
  onToggle,
}: {
  material: Material;
  open: boolean;
  onToggle: () => void;
}): React.JSX.Element {
  const { Icon, label, chip, badge } = kindMeta(material.kind);
  const markSeen = useMarkSeen();

  // Link / video-with-url: a real anchor to a new tab — no inline expand.
  const externalKind =
    material.kind === 'link' || (material.kind === 'video' && material.externalUrl != null);

  // page / file: an in-app inline expand below the row.
  const expandable = material.kind === 'page' || material.kind === 'file';

  // A leading tinted icon chip.
  const iconChip = (
    <span
      className={cn('flex size-11 shrink-0 items-center justify-center rounded-xl', chip)}
      aria-hidden="true"
    >
      <Icon className="size-5" />
    </span>
  );

  const kindBadge = (
    <Badge variant={badge} className="uppercase tracking-wide">
      {label}
    </Badge>
  );

  function onKeyDown(e: React.KeyboardEvent): void {
    if (e.key === 'Escape' && open) {
      onToggle();
      e.stopPropagation();
    }
  }

  return (
    <li className="px-4 py-3" onKeyDown={onKeyDown}>
      {externalKind && material.externalUrl ? (
        <a
          href={material.externalUrl}
          target="_blank"
          rel="noreferrer"
          data-testid="material-link"
          onClick={() => markSeen(material.itemId)}
          className="press group flex items-center gap-4 rounded-lg transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {iconChip}
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5 font-semibold leading-snug">
              <span className="truncate">{material.title}</span>
              <ExternalLink
                className="size-3.5 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
            </span>
            <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {kindBadge}
              <span className="truncate">{material.courseTitle}</span>
            </span>
          </span>
          <span
            className={cn(
              buttonVariants({ variant: 'outline', size: 'sm' }),
              'pointer-events-none shrink-0',
            )}
            aria-hidden="true"
          >
            {material.kind === 'video' ? 'Watch' : 'Open'}
          </span>
        </a>
      ) : expandable ? (
        <>
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            data-testid="material-expand"
            className="press flex w-full items-center gap-4 rounded-lg text-left transition-colors hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {iconChip}
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold leading-snug">{material.title}</span>
              <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                {kindBadge}
                <span className="truncate">{material.courseTitle}</span>
              </span>
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <span className="hidden text-sm font-medium text-muted-foreground sm:inline">
                {open ? 'Hide' : material.kind === 'page' ? 'Read' : 'Preview'}
              </span>
              <ChevronDown
                className={cn(
                  'size-4 text-muted-foreground transition-transform',
                  open && 'rotate-180',
                )}
                aria-hidden="true"
              />
            </span>
          </button>

          {open ? (
            <div className="mt-3 border-t pt-3" data-testid="material-expanded">
              {material.kind === 'page' ? (
                <PageBody itemId={material.itemId} hasBody={material.hasBody}>
                  {(text) => <RichText text={text} />}
                </PageBody>
              ) : material.fileObjectId ? (
                <MaterialFilePreview
                  fileObjectId={material.fileObjectId}
                  contentType={material.contentType}
                  filename={material.title}
                  itemId={material.itemId}
                />
              ) : (
                <p className="text-sm text-muted-foreground">No file attached.</p>
              )}
            </div>
          ) : null}
        </>
      ) : (
        // Bare item (e.g. a video without a URL): non-interactive row.
        <div className="flex items-center gap-4">
          {iconChip}
          <span className="min-w-0 flex-1">
            <span className="block truncate font-semibold leading-snug">{material.title}</span>
            <span className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {kindBadge}
              <span className="truncate">{material.courseTitle}</span>
            </span>
          </span>
        </div>
      )}
    </li>
  );
}

function MaterialsSkeleton(): React.JSX.Element {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <Skeleton className="h-11 flex-1" />
        <Skeleton className="h-11 w-72" />
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-3 px-1">
          <Skeleton className="size-10 shrink-0 rounded-xl" />
          <div className="space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <Card className="card-soft overflow-hidden p-0">
          <ul className="divide-y divide-border">
            {Array.from({ length: 4 }, (_, i) => (
              <li key={i} className="flex items-center gap-4 px-4 py-3">
                <Skeleton className="size-11 shrink-0 rounded-xl" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-4 w-2/3" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
                <Skeleton className="h-9 w-20 shrink-0" />
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function ErrorCard({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.JSX.Element {
  return (
    <Card>
      <div className="flex flex-col items-center gap-3 px-6 py-8 text-center">
        <XCircle className="size-6 text-destructive" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    </Card>
  );
}
